import { templateApiPathByPrefixId, v2ApiPathTemplatesPathByPathId } from "helpers/url/api/v2";
import apiClient from "services/ApiClient";
import { describeNetworkingError } from "services/ApiClient/describeNetworkingError";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PathId } from "features/Path/models";
import { useToast } from "@chakra-ui/react";
import { API_ROUTES } from "definitions/constants/routeConstants";
import { pathKeys } from "features/Path/hooks";
import { useNavigate } from "react-router";
import { titleize } from "helpers/string";
import useCaminoStore from "hooks/useCaminoStore";
import { templatePathByTemplateOrSummary } from "helpers/url/pages/v2/actionPaths";
import { Template, TemplatePrefixId, TemplateSummary } from "./models";
import { EmailTemplate } from "../EmailTemplate/models";
import { MessageTemplate } from "../MessageTemplate/models";
import { TaskTemplate } from "../TaskTemplate/models";
import { emailTemplateKeys } from "../EmailTemplate/hooks";
import { taskTemplateKeys } from "../TaskTemplate/hooks";
import { messageTemplateKeys } from "../MessageTemplate/hooks";
import { checkpointTemplateKeys } from "../CheckpointTemplate/hooks";
import { extractTemplate, fromTemplatePrefixId } from "./helpers";
import { TemplateType } from "../Shared/models";
import { meetingTemplateKeys } from "../MeetingTemplate/hooks";

export const sharedTemplateKeys = {
  all: ["sharedTemplates"] as const,
  lists: () => [...sharedTemplateKeys.all, "list"] as const,
  list: (filters: string) => [...sharedTemplateKeys.lists(), filters] as const,
  byPaths: () => [...sharedTemplateKeys.lists(), "paths"] as const,
  byPathId: (id: string) => [...sharedTemplateKeys.byPaths(), id] as const,
};

type TemplateKeySet =
  | typeof emailTemplateKeys
  | typeof taskTemplateKeys
  | typeof messageTemplateKeys
  | typeof checkpointTemplateKeys;

const getKeyMap = () => ({
  meeting_template: meetingTemplateKeys,
  message_template: messageTemplateKeys,
  email_template: emailTemplateKeys,
  task_template: taskTemplateKeys,
  checkpoint_template: checkpointTemplateKeys,
});

export const getKeySetByTemplatePrefixId = (templatePrefixId: TemplatePrefixId | undefined): TemplateKeySet | null => {
  const { templateType } = fromTemplatePrefixId(templatePrefixId);
  const keySet = getKeyMap()[templateType as TemplateType];
  if (!keySet) {
    console.error(
      `[getKeySetByTemplatePrefixId] No key set found for templateType "${templateType}" (prefixId: ${templatePrefixId})`,
    );
  }
  return keySet || null;
};

export const getDetailKeyByTemplatePrefixId = (templatePrefixId: TemplatePrefixId | undefined): readonly string[] => {
  const { id } = fromTemplatePrefixId(templatePrefixId);
  const keySet = getKeySetByTemplatePrefixId(templatePrefixId);
  if (!keySet) {
    throw new Error(`No key set found for prefix: ${templatePrefixId}`);
  }
  return keySet?.detail(id as string);
};

export const useSharedTemplatesByPathIdQuery = (pathId: PathId, options = {}) =>
  useQuery<TemplateSummary[]>({
    queryKey: sharedTemplateKeys.byPathId(pathId),
    queryFn: async () => apiClient.get(v2ApiPathTemplatesPathByPathId(pathId)),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
    enabled: !!pathId && ((options as { enabled?: boolean }).enabled ?? true),
  });

export const useListSharedTemplatesQuery = (options = {}) =>
  useQuery<TemplateSummary[]>({
    queryKey: sharedTemplateKeys.lists(),
    queryFn: async () => apiClient.get(API_ROUTES.templates.base),
    staleTime: 1000 * 60 * 3, // 3 minutes
    ...options,
  });

export function useDeleteTemplate(templateSummary: TemplateSummary | Template, onSuccess?: () => void) {
  const resolvedTemplate = extractTemplate(templateSummary);
  const deletePath = resolvedTemplate ? templateApiPathByPrefixId(resolvedTemplate?.prefixId) : "";
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async () => apiClient.delete<EmailTemplate | MessageTemplate | TaskTemplate>(deletePath),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: sharedTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: pathKeys.details() });
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: messageTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: taskTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: meetingTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: checkpointTemplateKeys.all });
      onSuccess?.();
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

export function useDuplicateTemplate(templateSummary: TemplateSummary | Template, onSuccess?: () => void) {
  const resolvedTemplate = extractTemplate(templateSummary);
  const { templateType } = fromTemplatePrefixId(resolvedTemplate.prefixId);
  const duplicatePath = resolvedTemplate ? `${templateApiPathByPrefixId(resolvedTemplate?.prefixId)}/duplicate` : "";
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  return useMutation({
    mutationFn: async (data: { name: string }) =>
      apiClient.post<EmailTemplate | MessageTemplate | TaskTemplate>(duplicatePath, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: sharedTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: pathKeys.details() });
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: messageTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: taskTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: meetingTemplateKeys.all });
      onSuccess?.();
      toast({
        title: `${titleize(templateType as string)} Duplicated`,
        status: "success",
      });
      navigate(templatePathByTemplateOrSummary(res));
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

export function useTestSendTemplate(templateSummary: TemplateSummary | Template, onSuccess?: () => void) {
  const resolvedTemplate = extractTemplate(templateSummary);
  const { resourceType } = fromTemplatePrefixId(resolvedTemplate.prefixId);
  const testSendPath = resolvedTemplate ? `${templateApiPathByPrefixId(resolvedTemplate?.prefixId)}/test-send` : "";
  const currentUser = useCaminoStore((state) => state.currentUser);
  const navigate = useNavigate();
  const toast = useToast();
  return useMutation({
    mutationFn: async () => apiClient.post<EmailTemplate | MessageTemplate | TaskTemplate>(testSendPath, {}),
    onSuccess: (res) => {
      onSuccess?.();
      toast({
        title: `Test send sent!`,
        description: `A test ${titleize(resourceType as string)} was sent to ${resourceType === "email" ? currentUser?.workEmail : currentUser?.fullName}`,
        status: "success",
      });
      navigate(templateApiPathByPrefixId(res.prefixId));
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: describeNetworkingError(e),
        status: "error",
      });
    },
  });
}

export const useTemplateDetailQueryByPrefixId = (templatePrefixId: TemplatePrefixId | undefined, options = {}) =>
  useQuery<Template | null>({
    queryKey: getDetailKeyByTemplatePrefixId(templatePrefixId),
    queryFn: async () => apiClient.get(templateApiPathByPrefixId(templatePrefixId)),
    staleTime: 1000 * 60 * 3, // 3 minutes
    enabled: !!templatePrefixId && ((options as { enabled?: boolean }).enabled ?? true),
    ...options,
  });
